import {
    CountryCode,
    CountryKeyValue,
    DEFAULT_COUNTRY,
    Language,
    LanguageCode,
    Locale,
    SUPPORTED_LANGUAGES,
    SUPPORTED_LOCALES,
} from '@headless-workspace/config';

const LOCALE_SEPARATOR = '-';

const UNSUPPORTED_LOCALE_ERROR = 'Unsupported locale';

/**
 * Check if the given value is a valid locale
 * @param {string} value
 * @return {boolean} true if the value is a valid locale, false otherwise
 */
const isLocale = (value: string): value is Locale => {
    return Object.values(SUPPORTED_LOCALES).includes(value as Locale);
};

/**
 * Convert a string to a Locale
 * @param locale
 * @return {Locale} locale if valid, otherwise throw an error
 */
const toLocale = (locale: string): Locale => {
    if (isLocale(locale)) {
        return locale as Locale;
    }
    throw new Error(`${UNSUPPORTED_LOCALE_ERROR} ${locale}`);
};

/**
 * Parse a locale string to retrieve the language
 * Example : `fr-FR` will be parsed to `fr`, `es-ES` will be parsed to `es`
 * @param {string} locale - Locale string or Language, example: `fr-FR`, `fr_FR`
 * @param {string} separator - The separator used in the locale string, default is '-'
 * @return {LanguageCode} language
 */

const parseLanguage = (locale: Locale | string, separator: string = LOCALE_SEPARATOR): LanguageCode => {
    const [language] = locale.split(separator);
    return language as LanguageCode;
};

/**
 * Parse a locale string to retrieve the country code
 * Example : `fr-FR` will be parsed to `FR`, fr-ES will be parsed to `ES`
 * @param {string} locale
 * @return {CountryCode} country code
 */
const parseCountry = (locale: Locale): CountryCode => {
    const [, country] = locale.split(LOCALE_SEPARATOR);
    return (country as CountryCode) ?? DEFAULT_COUNTRY;
};

const retrieveDataByCountry = <T>(json: CountryKeyValue<T>, locale: Locale): T | null => {
    if (!json || Object.keys(json).length === 0) {
        return null;
    }
    const countryCode = parseCountry(locale);
    if (json[countryCode] !== undefined) {
        return json[countryCode];
    }

    const language = parseLanguage(locale);
    if (json[language] !== undefined) {
        return json[language];
    }

    return null;
};

const mapLocaleToLanguage = (locale: Locale): string => {
    const localeMapping: Record<string, string> = {
        [SUPPORTED_LOCALES.fr_CH]: SUPPORTED_LANGUAGES.fr,
    };

    return localeMapping[locale as string] || '';
};

export const getMappedLocale = (headers: Headers, locale: Locale | Language): Locale => {
    const host = `${headers.get('x-forwarded-host')}`;

    if (host.endsWith('.ch')) {
        const localeMapping: Record<string, string> = {
            [SUPPORTED_LANGUAGES.fr]: SUPPORTED_LOCALES.fr_CH,
        };

        return (localeMapping[locale] as Locale) || locale;
    }

    return locale as Locale;
};

export const LanguageHelpers = {
    isLocale,
    parseLanguage,
    parseCountry,
    retrieveDataByCountry,
    mapLocaleToLanguage,
    getMappedLocale,
    toLocale,
};
